// Crafting recipes and the matcher. A recipe is either "shaped" (the ingredient
// positions matter, like a pickaxe) or "shapeless" (only the set of ingredients
// matters, like planks from a log). Everything is data — adding a recipe is one
// entry — and matching is pure, so it's unit-tested independently of the UI.

import { Block } from './blocks';
import { Item } from './items';

export interface Output {
  item: number;
  count: number;
}

interface ShapedRecipe {
  kind: 'shaped';
  cells: (number | null)[]; // trimmed row-major grid
  w: number;
  h: number;
  out: Output;
}

interface ShapelessRecipe {
  kind: 'shapeless';
  items: number[]; // multiset of ingredient ids
  out: Output;
}

type Recipe = ShapedRecipe | ShapelessRecipe;

/** Trim a w×h grid down to the bounding box of its non-null cells. */
function trim(
  cells: (number | null)[],
  w: number,
  h: number,
): { cells: (number | null)[]; w: number; h: number } {
  let minX = w;
  let minY = h;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (cells[y * w + x] != null) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }
  }
  if (maxX < 0) return { cells: [], w: 0, h: 0 };
  const tw = maxX - minX + 1;
  const th = maxY - minY + 1;
  const out: (number | null)[] = [];
  for (let y = minY; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) out.push(cells[y * w + x]);
  }
  return { cells: out, w: tw, h: th };
}

/** Build a shaped recipe from a small pattern + legend. '.'/' ' means empty. */
function shaped(pattern: string[], legend: Record<string, number>, out: Output): ShapedRecipe {
  const h = pattern.length;
  const w = Math.max(...pattern.map((r) => r.length));
  const cells: (number | null)[] = [];
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const ch = pattern[y][x] ?? ' ';
      cells.push(ch === ' ' || ch === '.' ? null : (legend[ch] ?? null));
    }
  }
  const t = trim(cells, w, h);
  return { kind: 'shaped', cells: t.cells, w: t.w, h: t.h, out };
}

function shapeless(items: number[], out: Output): ShapelessRecipe {
  return { kind: 'shapeless', items, out };
}

const P = { P: Block.Planks };
const PS = { P: Block.Planks, S: Item.Stick };
const CS = { C: Block.Cobblestone, S: Item.Stick };

export const RECIPES: Recipe[] = [
  // Raw materials.
  shapeless([Block.Wood], { item: Block.Planks, count: 4 }),
  shaped(['P', 'P'], P, { item: Item.Stick, count: 4 }),
  shaped(['PP', 'PP'], P, { item: Block.CraftingTable, count: 1 }),
  shaped(['P', 'S'], PS, { item: Block.Torch, count: 4 }),
  // Wooden tools.
  shaped(['PPP', ' S ', ' S '], PS, { item: Item.WoodPickaxe, count: 1 }),
  shaped(['PP', 'PS', ' S'], PS, { item: Item.WoodAxe, count: 1 }),
  shaped(['PP', 'SP', 'S '], PS, { item: Item.WoodAxe, count: 1 }), // mirror
  shaped(['P', 'P', 'S'], PS, { item: Item.WoodSword, count: 1 }),
  // Stone tools.
  shaped(['CCC', ' S ', ' S '], CS, { item: Item.StonePickaxe, count: 1 }),
  shaped(['CC', 'CS', ' S'], CS, { item: Item.StoneAxe, count: 1 }),
  shaped(['CC', 'SC', 'S '], CS, { item: Item.StoneAxe, count: 1 }), // mirror
  shaped(['C', 'C', 'S'], CS, { item: Item.StoneSword, count: 1 }),
];

function sameMultiset(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  const sorted = (arr: number[]) => [...arr].sort((x, y) => x - y);
  const sa = sorted(a);
  const sb = sorted(b);
  return sa.every((v, i) => v === sb[i]);
}

/**
 * Match the 3×3 crafting grid (row-major, length 9, item ids or null) against
 * the recipe list. Returns the output, or null if nothing matches.
 */
export function matchRecipe(grid: (number | null)[]): Output | null {
  const t = trim(grid, 3, 3);
  const present = grid.filter((c): c is number => c != null);
  if (present.length === 0) return null;

  for (const r of RECIPES) {
    if (r.kind === 'shaped') {
      if (r.w === t.w && r.h === t.h && r.cells.every((c, i) => c === t.cells[i])) return r.out;
    } else if (sameMultiset(r.items, present)) {
      return r.out;
    }
  }
  return null;
}
